import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import * as Sentry from "@sentry/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/use-toast";
import { Bot, Loader2, Plus, Search, Users, User, Copy, UploadCloud } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../supabase/auth";
import { supabase } from "../../../supabase/supabase";
import { useOrganization } from "../../App";
import { publishAgent } from "../../services/agentPublishService";
import type {
  AgentPublishFeatureType,
  AgentPublishType,
  AgentPublishVersion,
} from "../../services/agentPublishService";
import PublishAgentDialog from "./PublishAgentDialog";

export type Assistant = {
  id: string;
  name: string;
  description: string | null;
  status: string;
  assistant_type?: 'single' | 'multi';
  llm_provider: string | null;
  llm_model: string | null;
  llm_configuration?: any;
  tts_provider: string | null;
  tts_model: string | null;
  tts_configuration?: any;
  stt_provider: string | null;
  stt_model: string | null;
  stt_configuration?: any;
  system_prompt?: string | null;
  first_message?: string | null;
  end_call_message?: string | null;
  summary_prompt?: string | null;
  evaluation_prompt?: string | null;
  structured_data_prompt?: string | null;
  structured_json_schema?: string | null;
  idle_message?: string | null;
  idle_hangup_message?: string | null;
  configuration?: any;
  variables?: any;
  use_integrated_agent?: boolean | null;
  model_provider?: string | null;
  openrouter_api_key?: string | null;
  openrouter_model?: string | null;
  organization_id?: string;
  created_by?: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
};

interface AssistantsListProps {
  onAssistantSelect?: (id: string) => void;
  selectedAssistantId?: string | null;
  className: string | null;
  filteredAssistants: Assistant[];
  setFilteredAssistants: (assistants: Assistant[]) => void;
  showTypeSelectionDialog: boolean;
  setShowTypeSelectionDialog: (show: boolean) => void;
}

const PAGE_SIZE = 10;

function escapeIlike(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/%/g, "\\%").replace(/_/g, "\\_");
}

export default function AssistantsList({
  onAssistantSelect,
  selectedAssistantId: propSelectedAssistantId,
  className = "",
  filteredAssistants,
  setFilteredAssistants,
  showTypeSelectionDialog,
  setShowTypeSelectionDialog,
}: AssistantsListProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentOrganization } = useOrganization();
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const searchQuery = searchParams.get("q") ?? "";
  const [isLoading, setIsLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(
    propSelectedAssistantId || null
  );
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showCloneDialog, setShowCloneDialog] = useState(false);
  const [newAssistantName, setNewAssistantName] = useState("");
  const [cloneAssistantName, setCloneAssistantName] = useState("");
  const [assistantType, setAssistantType] = useState<'single' | 'multi'>('single');
  const [assistantToClone, setAssistantToClone] = useState<Assistant | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const [publishingAssistantId, setPublishingAssistantId] = useState<string | null>(null);
  const [assistantToPublish, setAssistantToPublish] = useState<Assistant | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    if (user && currentOrganization) {
      fetchAssistants();
    }
  }, [user, currentOrganization]);

  useEffect(() => {
    const t = setTimeout(() => fetchAssistants(), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    setSelectedId(propSelectedAssistantId || null);
  }, [propSelectedAssistantId]);

  const fetchAssistants = async () => {
    setIsLoading(true);
    try {
      if (!currentOrganization) {
        setAssistants([]);
        setFilteredAssistants([]);
        setOffset(0);
        setHasMore(true);
        setIsLoading(false);
        return;
      }

      const term = searchQuery.trim();
      let pattern = term ? `%${escapeIlike(term)}%` : "";
      if (pattern && /[,.]/.test(pattern)) {
        pattern = `"${pattern.replace(/"/g, '""')}"`;
      }
      let query = supabase
        .from("assistants")
        .select("*")
        .order("created_at", { ascending: false })
        .range(0, PAGE_SIZE - 1);
      if (pattern) {
        query = query.or(
          `name.ilike.${pattern},description.ilike.${pattern},llm_provider.ilike.${pattern}`
        );
      }
      const { data: assistantsData, error: assistantsError } = await query;

      if (assistantsError) {
        setAssistants([]);
        setFilteredAssistants([]);
        setOffset(0);
        setHasMore(false);
        Sentry.logger.error('Error fetching assistants', { 
          error: assistantsError,
          log_source: 'assistants_list',
          organization_id: currentOrganization?.id
        });
        toast({
          title: "Error",
          description: "Failed to load assistants. Please try again.",
          variant: "destructive",
        });
      } else if (assistantsData) {
        setAssistants(assistantsData);
        setFilteredAssistants(assistantsData);
        setOffset(assistantsData.length);
        setHasMore(assistantsData.length === PAGE_SIZE);
      }
    } catch (error) {
      Sentry.logger.error('Error fetching assistants in catch block', { 
        error,
        log_source: 'assistants_list',
        organization_id: currentOrganization?.id
      });
      toast({
        title: "Error",
        description: "An unexpected error occurred while loading assistants.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreAssistants = async () => {
    if (!currentOrganization || !hasMore || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const term = (new URLSearchParams(window.location.search).get("q") ?? "").trim();
      let pattern = term ? `%${escapeIlike(term)}%` : "";
      if (pattern && /[,.]/.test(pattern)) {
        pattern = `"${pattern.replace(/"/g, '""')}"`;
      }
      let query = supabase
        .from("assistants")
        .select("*")
        .order("created_at", { ascending: false })
        .range(offset, offset + PAGE_SIZE - 1);
      if (pattern) {
        query = query.or(
          `name.ilike.${pattern},description.ilike.${pattern},llm_provider.ilike.${pattern}`
        );
      }
      const { data, error } = await query;

      if (error) {
        Sentry.logger.error('Error loading more assistants', { error, log_source: 'assistants_list' });
        toast({ title: "Error", description: "Failed to load more assistants.", variant: "destructive" });
        return;
      }
      const next = data ?? [];
      setAssistants((prev) => {
        const full = [...prev, ...next];
        setFilteredAssistants(full);
        return full;
      });
      setOffset((o) => o + next.length);
      setHasMore(next.length === PAGE_SIZE);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleCreateAssistant = () => {
    setShowTypeSelectionDialog(true);
  };

  const handleTypeSelection = (type: 'single' | 'multi') => {
    setAssistantType(type);
    setShowTypeSelectionDialog(false);
    setShowCreateDialog(true);
  };

  const handleCreateSubmit = async () => {
    const trimmedName = newAssistantName.trim();

    if (!trimmedName) {
      toast({
        title: "Name Required",
        description: "Please enter a name for your assistant",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);
    try {
      if (!currentOrganization?.id) {
        throw new Error("Organization required to create assistant");
      }

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        throw new Error("User authentication required");
      }

      
      try {
        const { data: existingAssistants, error: existingError } = await supabase
          .from("assistants")
          .select("id, name")
          .eq("organization_id", currentOrganization.id)
          .ilike("name", trimmedName)
          .limit(1);

        if (existingError) {
          console.warn("Error checking for duplicate assistant name", existingError);
        } else if (existingAssistants && existingAssistants.length > 0) {
          toast({
            title: "Duplicate Name",
            description:
              "An assistant with this name already exists in your organization. Please choose a different name.",
            variant: "destructive",
          });
          return;
        }
      } catch (checkError) {
        Sentry.logger.error("Unexpected error during duplicate name check", {
          error: checkError,
          log_source: "assistants_list",
          assistant_name: trimmedName,
          organization_id: currentOrganization.id,
        });
      }

      const now = new Date().toISOString();

      const { data: assistant, error } = await supabase
        .from("assistants")
        .insert({
          name: trimmedName,
          assistant_type: assistantType,
          llm_provider: "openai",
          llm_model: "gpt-4",
          tts_provider: "elevenlabs",
          stt_provider: "deepgram",
          status: "draft",
          organization_id: currentOrganization.id,
          created_by: userData.user.id,
          updated_by: userData.user.id,
          created_at: now,
          updated_at: now,
          configuration: {},
          llm_configuration: {},
          stt_configuration: {},
          tts_configuration: {},
          variables: [],
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      await supabase.from("assistant_versions").insert({
        assistant_id: assistant.id,
        version_number: 1,
        configuration: {},
        llm_configuration: {},
        stt_configuration: {},
        tts_configuration: {},
        notes: "Initial creation",
        created_by: userData.user.id,
      });

      await supabase.from("audit_logs").insert({
        user_id: userData.user.id,
        action: "create",
        resource_type: "assistant",
        resource_id: assistant.id,
        details: {
          name: newAssistantName,
          requested_org_id: null,
          actual_org_id: null,
        },
      });

      toast({
        title: "Success! 🎉",
        description: `Assistant "${newAssistantName}" has been created. You can now customize it.`,
      });

      setShowCreateDialog(false);
      setNewAssistantName("");
      setAssistantType('single');

      await fetchAssistants();

      const qs = searchParams.toString();
      navigate(`/assistants/${assistant.id}${qs ? `?${qs}` : ""}`);
    } catch (error: any) {
      Sentry.logger.error('Error creating assistant', { 
        error,
        log_source: 'assistants_list',
        assistant_name: newAssistantName,
        assistant_type: assistantType,
        organization_id: currentOrganization?.id
      });
      toast({
        title: "Creation Failed",
        description: error.message || "Failed to create assistant",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditAssistant = (assistantId: string) => {
    setSelectedId(assistantId);
    if (onAssistantSelect) {
      onAssistantSelect(assistantId);
    } else {
      const qs = searchParams.toString();
      navigate(`/assistants/${assistantId}${qs ? `?${qs}` : ""}`);
    }
  };

  const openPublishDialog = (assistant: Assistant, e: React.MouseEvent) => {
    e.stopPropagation();
    setAssistantToPublish(assistant);
  };

  const handlePublishAssistant = async ({
    type,
    featureType,
    version,
  }: {
    type: AgentPublishType;
    featureType: AgentPublishFeatureType;
    version: AgentPublishVersion;
  }) => {
    if (!assistantToPublish) return;

    setPublishingAssistantId(assistantToPublish.id);
    try {
      await publishAgent({
        name: assistantToPublish.name,
        sourceId: assistantToPublish.id,
        source: "voxi",
        type,
        featureType,
        version,
      });

      const { error } = await supabase
        .from("assistants")
        .update({ status: "published", updated_at: new Date().toISOString() })
        .eq("id", assistantToPublish.id);

      if (error) {
        throw error;
      }

      const updateAssistantStatus = (items: Assistant[]) =>
        items.map((item) =>
          item.id === assistantToPublish.id ? { ...item, status: "published" } : item
        );

      setAssistants((prev) => updateAssistantStatus(prev));
      setFilteredAssistants(updateAssistantStatus(filteredAssistants));

      toast({
        title: "Assistant Published",
        description: `"${assistantToPublish.name}" has been published successfully.`,
      });
      setAssistantToPublish(null);
    } catch (error: any) {
      Sentry.logger.error("Error publishing assistant", {
        error,
        log_source: "assistants_list",
        assistant_id: assistantToPublish.id,
        organization_id: currentOrganization?.id,
      });
      toast({
        title: "Publish Failed",
        description: error.message || "Failed to publish assistant",
        variant: "destructive",
      });
    } finally {
      setPublishingAssistantId(null);
    }
  };

  const handleCloneAssistant = async (assistant: Assistant, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the card click
    
    try {
      // Fetch full assistant data including all fields
      const { data: fullAssistant, error } = await supabase
        .from("assistants")
        .select("*")
        .eq("id", assistant.id)
        .single();

      if (error) {
        throw error;
      }

      setAssistantToClone(fullAssistant);
      setCloneAssistantName(`${fullAssistant.name} (Copy)`);
      setShowCloneDialog(true);
    } catch (error: any) {
      Sentry.logger.error('Error preparing assistant for cloning', { 
        error,
        log_source: 'assistants_list',
        assistant_id: assistant.id,
        organization_id: currentOrganization?.id
      });
      toast({
        title: "Error",
        description: "Failed to prepare assistant for cloning. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCloneSubmit = async () => {
    if (!cloneAssistantName.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter a name for the cloned assistant",
        variant: "destructive",
      });
      return;
    }

    if (!assistantToClone) {
      toast({
        title: "Error",
        description: "No assistant selected for cloning",
        variant: "destructive",
      });
      return;
    }

    setIsCloning(true);
    try {
      if (!currentOrganization?.id) {
        throw new Error("Organization required to clone assistant");
      }

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) {
        throw new Error("User authentication required");
      }

      const now = new Date().toISOString();

      // Clone the assistant with all settings except identity fields
      const clonedData = {
        name: cloneAssistantName,
        assistant_type: assistantToClone.assistant_type,
        description: assistantToClone.description,
        llm_provider: assistantToClone.llm_provider,
        llm_model: assistantToClone.llm_model,
        llm_configuration: assistantToClone.llm_configuration,
        stt_provider: assistantToClone.stt_provider,
        stt_model: assistantToClone.stt_model,
        stt_configuration: assistantToClone.stt_configuration,
        tts_provider: assistantToClone.tts_provider,
        tts_model: assistantToClone.tts_model,
        tts_configuration: assistantToClone.tts_configuration,
        system_prompt: assistantToClone.system_prompt,
        first_message: assistantToClone.first_message,
        end_call_message: assistantToClone.end_call_message,
        summary_prompt: assistantToClone.summary_prompt,
        evaluation_prompt: assistantToClone.evaluation_prompt,
        structured_data_prompt: assistantToClone.structured_data_prompt,
        structured_json_schema: assistantToClone.structured_json_schema,
        idle_message: assistantToClone.idle_message,
        idle_hangup_message: assistantToClone.idle_hangup_message,
        configuration: assistantToClone.configuration,
        variables: assistantToClone.variables,
        use_integrated_agent: assistantToClone.use_integrated_agent,
        model_provider: assistantToClone.model_provider,
        openrouter_api_key: assistantToClone.openrouter_api_key,
        openrouter_model: assistantToClone.openrouter_model,
        status: "draft", // Always set to draft for cloned assistants
        organization_id: currentOrganization.id,
        created_by: userData.user.id,
        updated_by: userData.user.id,
        created_at: now,
        updated_at: now,
      };

      const { data: newAssistant, error } = await supabase
        .from("assistants")
        .insert(clonedData)
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Create initial version for the cloned assistant
      await supabase.from("assistant_versions").insert({
        assistant_id: newAssistant.id,
        version_number: 1,
        configuration: assistantToClone.configuration || {},
        llm_configuration: assistantToClone.llm_configuration || {},
        stt_configuration: assistantToClone.stt_configuration || {},
        tts_configuration: assistantToClone.tts_configuration || {},
        notes: `Cloned from ${assistantToClone.name}`,
        created_by: userData.user.id,
      });

      // Log the clone action
      await supabase.from("audit_logs").insert({
        user_id: userData.user.id,
        action: "clone",
        resource_type: "assistant",
        resource_id: newAssistant.id,
        details: {
          name: cloneAssistantName,
          cloned_from: assistantToClone.id,
          cloned_from_name: assistantToClone.name,
          requested_org_id: null,
          actual_org_id: null,
        },
      });

      toast({
        title: "Success! 🎉",
        description: `Assistant "${cloneAssistantName}" has been cloned successfully. You can now customize it.`,
      });

      setShowCloneDialog(false);
      setCloneAssistantName("");
      setAssistantToClone(null);

      await fetchAssistants();

      const qs = searchParams.toString();
      navigate(`/assistants/${newAssistant.id}${qs ? `?${qs}` : ""}`);
    } catch (error: any) {
      Sentry.logger.error('Error cloning assistant', { 
        error,
        log_source: 'assistants_list',
        clone_name: cloneAssistantName,
        source_assistant_id: assistantToClone?.id,
        organization_id: currentOrganization?.id
      });
      toast({
        title: "Clone Failed",
        description: error.message || "Failed to clone assistant",
        variant: "destructive",
      });
    } finally {
      setIsCloning(false);
    }
  };


  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "published":
        return "border-good-line bg-good-bg text-good";
      case "draft":
        return "border-mid-line bg-mid-bg text-mid";
      case "archived":
        return "border-weak-line bg-weak-bg text-weak";
      default:
        return "border-weak-line bg-weak-bg text-weak";
    }
  };

  return (
    <div className={`${className} h-full flex flex-col`}>
      <div className="pr-6 py-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search assistants..."
            value={searchQuery}
            onChange={(e) => {
              const value = e.target.value;
              setSearchParams(
                (prev) => {
                  const next = new URLSearchParams(prev);
                  if (value.trim() === "") next.delete("q");
                  else next.set("q", value);
                  return next;
                },
                { replace: true }
              );
            }}
            className="pl-10 bg-card border-border text-foreground w-full"
          />
        </div>
      </div>
      <Separator className="-ml-6" />
      <div className="flex-1 overflow-y-auto pr-6 py-3 space-y-1.5">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {filteredAssistants.map((assistant) => (
              <Card
                key={assistant.id}
                selected={selectedId === assistant.id}
                className="gap-0 py-0 shadow-none transition-colors cursor-pointer hover:bg-secondary/60"
                onClick={() => handleEditAssistant(assistant.id)}
              >
                <CardContent className="px-3 py-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      {/* name owns the full first line so it doesn't truncate */}
                      <h3
                        className="truncate text-sm font-medium text-foreground"
                        title={assistant.name}
                      >
                        {assistant.name}
                      </h3>

                      {/* badges share the short meta line */}
                      <div className="flex min-w-0 items-center gap-1.5">
                        {assistant.assistant_type === 'multi' && (
                          <span className="inline-flex shrink-0 items-center rounded-full border border-border px-1.5 py-px text-[11px] font-medium text-muted-foreground">
                            <Users className="mr-0.5 h-2.5 w-2.5" />
                            Multi
                          </span>
                        )}
                        <span
                          className={`inline-flex shrink-0 items-center rounded-full border px-1.5 py-px text-[11px] font-medium ${getStatusBadgeClass(
                            assistant.status
                          )}`}
                        >
                          {assistant.status}
                        </span>
                        <p className="truncate text-xs text-muted-foreground">
                          {assistant.llm_provider} / {assistant.llm_model}
                          {assistant.tts_provider ? ` \u00b7 ${assistant.tts_provider}` : ''}
                        </p>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-0.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => openPublishDialog(assistant, e)}
                        disabled={publishingAssistantId === assistant.id}
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                        title="Publish assistant"
                      >
                        {publishingAssistantId === assistant.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <UploadCloud className="h-3 w-3" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleCloneAssistant(assistant, e)}
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                        title="Clone assistant"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {hasMore && assistants.length > 0 && (
              <div className="flex justify-center py-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadMoreAssistants}
                  disabled={isLoadingMore}
                  className="w-full border-border text-foreground hover:bg-primary/10"
                >
                  {isLoadingMore ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Load more"
                  )}
                </Button>
              </div>
            )}

            {assistants.length === 0 && (
              <div className="text-center py-6">
                <Bot className="mx-auto h-8 w-8 text-primary mb-2" />
                <h3 className="text-base font-medium mb-2">
                  No assistants found
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Create your first assistant to get started
                </p>
                <Button
                  onClick={handleCreateAssistant}
                  className="bg-primary hover:bg-primary/80 text-primary-foreground font-medium text-sm w-full"
                >
                  Create Assistant
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Type Selection Dialog */}
      <Dialog open={showTypeSelectionDialog} onOpenChange={setShowTypeSelectionDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg">Choose Assistant Type</DialogTitle>
            <DialogDescription className="text-sm">
              Select whether you want to create a single assistant or a multi-agent assistant
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <button
              onClick={() => handleTypeSelection('single')}
              className="flex flex-col items-center justify-center p-6 border-2 border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer"
            >
              <User className="h-8 w-8 mb-2 text-primary" />
              <h3 className="font-semibold">Single Assistant</h3>
              <p className="text-xs text-muted-foreground text-center mt-1">
                A single AI assistant with one persona
              </p>
            </button>
            <button
              onClick={() => handleTypeSelection('multi')}
              className="flex flex-col items-center justify-center p-6 border-2 border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer"
            >
              <Users className="h-8 w-8 mb-2 text-primary" />
              <h3 className="font-semibold">Multi-Agent</h3>
              <p className="text-xs text-muted-foreground text-center mt-1">
                Multiple sub-agents working together
              </p>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-lg">
              Create New {assistantType === 'multi' ? 'Multi-Agent' : 'Single'} Assistant
            </DialogTitle>
            <DialogDescription className="text-sm">
              Give your {assistantType === 'multi' ? 'multi-agent' : ''} assistant a name to get started. 
              {assistantType === 'multi' 
                ? ' You can add sub-agents after creation.'
                : ' You can customize its settings afterward.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Enter assistant name..."
              value={newAssistantName}
              onChange={(e) => setNewAssistantName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleCreateSubmit();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateSubmit}
              disabled={isCreating || !newAssistantName.trim()}
              className="bg-primary hover:bg-primary/80 text-primary-foreground"
            >
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create & Continue"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clone Dialog */}
      <Dialog open={showCloneDialog} onOpenChange={setShowCloneDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-lg">Clone Assistant</DialogTitle>
            <DialogDescription className="text-sm">
              Create a copy of "{assistantToClone?.name}" with all its settings including prompts, configurations, and model selections.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Enter cloned assistant name..."
              value={cloneAssistantName}
              onChange={(e) => setCloneAssistantName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleCloneSubmit();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCloneDialog(false)}
              disabled={isCloning}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCloneSubmit}
              disabled={isCloning || !cloneAssistantName.trim()}
              className="bg-primary hover:bg-primary/80 text-primary-foreground"
            >
              {isCloning ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cloning...
                </>
              ) : (
                <>
                  <Copy className="mr-2 h-4 w-4" />
                  Clone Assistant
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PublishAgentDialog
        assistantName={assistantToPublish?.name}
        open={Boolean(assistantToPublish)}
        isPublishing={Boolean(
          assistantToPublish && publishingAssistantId === assistantToPublish.id
        )}
        onOpenChange={(open) => {
          if (!open && !publishingAssistantId) {
            setAssistantToPublish(null);
          }
        }}
        onPublish={handlePublishAssistant}
      />

    </div>
  );
}
